'use client'

import { useCallback, useMemo } from 'react'
import { IGetRestaurantsQueryParams } from '@/api/restaurants/get-restaurants-query'
import { IRestaurantCitySearchQuerySetters } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import {
    DEFAULT_RESTAURANT_SORTING,
    ICityUrlParams,
    RestaurantQuerySorting,
    RestaurantsCitySearchUrlParams,
} from '@/setup/urls/city-route'
import { useOptimisticSearchParamState } from '@/utils/url/use-optimistic-url-param-state'

interface UseRestaurantCityManageSearchQueryReturn {
    query: Omit<IGetRestaurantsQueryParams, 'page'>
    setters: IRestaurantCitySearchQuerySetters
}

/**
 * This hook manages the search query for the restaurant city search page.
 * It handles updated to the url, as well as the local state.
 *
 * If you want to add a new parameter to the search, add it here.
 */
export function useRestaurantCitySearchManager(): UseRestaurantCityManageSearchQueryReturn {
    const [rawSortBy, rawSetSortBy] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.SORT_BY,
        'search',
        'string',
        'replace'
    )

    const sortBy = useMemo(
        () =>
            Object.values(RestaurantQuerySorting).includes(rawSortBy as RestaurantQuerySorting)
                ? (rawSortBy as RestaurantQuerySorting)
                : DEFAULT_RESTAURANT_SORTING,
        [rawSortBy]
    )

    const [citySlug, rawSetCitySlug] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.CITY_SLUG,
        'path',
        'string',
        'push'
    )

    const [name, rawSetName] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.SEARCH,
        'search',
        'string',
        'replace'
    )

    const [rawOpenNow, rawSetOpenNow] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.OPEN_NOW,
        'search',
        'string',
        'replace'
    )

    const [tagSlugs, rawSetTagSlugs] = useOptimisticSearchParamState<string[]>(
        RestaurantsCitySearchUrlParams.TAGS,
        'search',
        'array',
        'replace'
    )

    const query = useMemo<ICityUrlParams>(
        () => ({
            citySlug: typeof citySlug === 'string' ? citySlug : '',
            name: name || undefined,
            tagSlugs: tagSlugs || undefined,
            sortBy,
            openNow: rawOpenNow === 'true' || undefined,
        }),
        [citySlug, name, tagSlugs, sortBy, rawOpenNow]
    )

    const setCitySlug = useCallback(
        (nextCitySlug: string) => {
            rawSetCitySlug(
                nextCitySlug,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, citySlug: nextCitySlug },
                },
                { scroll: false }
            )
        },
        [query, rawSetCitySlug]
    )

    const setName = useCallback(
        (nextName: string) => {
            rawSetName(
                nextName,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, name: nextName },
                },
                { scroll: false }
            )
        },

        [query, rawSetName]
    )

    const setTagSlugs = useCallback(
        (nextTagSlugs: string[]) => {
            rawSetTagSlugs(
                nextTagSlugs,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, tagSlugs: nextTagSlugs },
                },
                { scroll: false }
            )
        },
        [query, rawSetTagSlugs]
    )

    const setSortBy = useCallback(
        (nextSortBy: RestaurantQuerySorting) => {
            rawSetSortBy(
                nextSortBy,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, sortBy: nextSortBy },
                },
                { scroll: false }
            )
        },
        [query, rawSetSortBy]
    )

    const setOpenNow = useCallback(
        (nextOpenNow: boolean) => {
            rawSetOpenNow(
                nextOpenNow ? 'true' : '',
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, openNow: nextOpenNow },
                },
                { scroll: false }
            )
        },
        [query, rawSetOpenNow]
    )

    const setters = useMemo(
        () => ({
            setCitySlug,
            setName,
            setTagSlugs,
            setSortBy,
            setOpenNow,
        }),
        [setCitySlug, setName, setTagSlugs, setSortBy, setOpenNow]
    )

    return useMemo(
        () => ({
            query,
            setters,
        }),
        [query, setters]
    )
}
